/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { ContactResponse } from '../models/contact-response';
import { CreateContactRequest } from '../models/create-contact-request';
import { GenericResponse } from '../models/generic-response';
import { GetContactResponse } from '../models/get-contact-response';
import { UpdateContactRequest } from '../models/update-contact-request';

/**
 * Contact APIs
 */
@Injectable({ providedIn: 'root' })
export class ContactService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getAllContacts()` */
  static readonly GetAllContactsPath = '/api/v1/contacts';

  /**
   * Get list of contacts.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllContacts()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllContacts$Response(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields.
       *  - name
       *  - mobile number
       */
      search?: string;

      /**
       * this adds a filter to the following field:
       * - user group
       */
      userGroupId?: Array<number>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetContactResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ContactService.GetAllContactsPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.query('search', params.search, {});
      rb.query('userGroupId', params.userGroupId, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetContactResponse>;
        })
      );
  }

  /**
   * Get list of contacts.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllContacts$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllContacts(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields.
       *  - name
       *  - mobile number
       */
      search?: string;

      /**
       * this adds a filter to the following field:
       * - user group
       */
      userGroupId?: Array<number>;
    },
    context?: HttpContext
  ): Observable<GetContactResponse> {
    return this.getAllContacts$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<GetContactResponse>): GetContactResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `createContact()` */
  static readonly CreateContactPath = '/api/v1/contacts';

  /**
   * Create new Contact.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createContact()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createContact$Response(
    params: {
      body: CreateContactRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ContactResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ContactService.CreateContactPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ContactResponse>;
        })
      );
  }

  /**
   * Create new Contact.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createContact$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createContact(
    params: {
      body: CreateContactRequest;
    },
    context?: HttpContext
  ): Observable<ContactResponse> {
    return this.createContact$Response(params, context).pipe(
      map((r: StrictHttpResponse<ContactResponse>): ContactResponse => r.body)
    );
  }

  /** Path part for operation `getContactById()` */
  static readonly GetContactByIdPath = '/api/v1/contacts/{id}';

  /**
   * Get contact details using id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getContactById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getContactById$Response(
    params: {
      /**
       * The contact's id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ContactResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ContactService.GetContactByIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ContactResponse>;
        })
      );
  }

  /**
   * Get contact details using id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getContactById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getContactById(
    params: {
      /**
       * The contact's id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<ContactResponse> {
    return this.getContactById$Response(params, context).pipe(
      map((r: StrictHttpResponse<ContactResponse>): ContactResponse => r.body)
    );
  }

  /** Path part for operation `disableContact()` */
  static readonly DisableContactPath = '/api/v1/contacts/{id}';

  /**
   * disable contact.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `disableContact()` instead.
   *
   * This method doesn't expect any request body.
   */
  disableContact$Response(
    params: {
      /**
       * The contact's id that needs to be soft deleted
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ContactService.DisableContactPath,
      'delete'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * disable contact.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `disableContact$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  disableContact(
    params: {
      /**
       * The contact's id that needs to be soft deleted
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.disableContact$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `updateContactById()` */
  static readonly UpdateContactByIdPath = '/api/v1/contacts/{id}';

  /**
   * Update contact by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateContactById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateContactById$Response(
    params: {
      /**
       * The contact's id that needs to be updated
       */
      id: number;
      body: UpdateContactRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ContactResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ContactService.UpdateContactByIdPath,
      'patch'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ContactResponse>;
        })
      );
  }

  /**
   * Update contact by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateContactById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateContactById(
    params: {
      /**
       * The contact's id that needs to be updated
       */
      id: number;
      body: UpdateContactRequest;
    },
    context?: HttpContext
  ): Observable<ContactResponse> {
    return this.updateContactById$Response(params, context).pipe(
      map((r: StrictHttpResponse<ContactResponse>): ContactResponse => r.body)
    );
  }
}
